import type { INotificationRule, INotificationRuleTemplate } from '@condo/domain';
import { Hash, List, Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { DataTable, type ColumnDef } from '@/shared/components/DataTable';
import type { INotificationTemplateWithRules } from '../../model';

interface NotificationSettingsTableProps {
    templatesWithRules: INotificationTemplateWithRules[];
    userUid: string;
    userEmail?: string;
    isLoading?: boolean;
    error?: Error | null;
    onRefetch: () => void;
    onGenerateRule: (params: { rules: { templateName: string; tokens: Record<string, unknown> }[]; userUid?: string; userEmail?: string }) => Promise<unknown>;
    onUpdateRule: (rule: INotificationRule) => Promise<unknown>;
    onDeleteRule: (ruleId: number) => Promise<unknown>;
}

export function NotificationSettingsTable({
    templatesWithRules,
    userUid,
    userEmail,
    isLoading = false,
    error,
    onRefetch,
    onGenerateRule,
    onUpdateRule,
    onDeleteRule,
}: NotificationSettingsTableProps) {
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

    const columns: ColumnDef<INotificationTemplateWithRules>[] = [
        {
            id: 'title',
            header: () => (
                <span className="flex items-center gap-2">
                    <List className="h-4 w-4 text-muted-foreground" />
                    Name
                </span>
            ),
            cell: row => row.title ?? row.name,
        },
        {
            id: 'description',
            header: () => (
                <span className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    Description
                </span>
            ),
            cell: row => <span className="text-sm italic text-muted-foreground">{row.description ?? row.name}</span>,
        },
        {
            id: 'rules',
            header: () => (
                <span className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    Active Rules
                </span>
            ),
            cell: row => (row.ruleCount === 0 ? 'No rules' : `${row.activeRuleCount}/${row.ruleCount} active rules`),
            align: 'center',
        },
        {
            id: 'add',
            header: () => (
                <span className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-muted-foreground" />
                    Actions
                </span>
            ),
            cell: row => (
                <Button variant="outline" size="sm" onClick={() => onGenerateRule({ rules: [{ templateName: row.name, tokens: {} }], userUid, userEmail })}>
                    <Plus className="mr-1 h-3 w-3" />
                    Add
                </Button>
            ),
            align: 'center',
        },
    ];

    return (
        <div className="max-h-[900px] overflow-auto rounded-md border">
            <DataTable<INotificationTemplateWithRules>
                columns={columns}
                data={templatesWithRules}
                getRowId={row => String(row.notificationRuleTemplateId ?? row.name)}
                isLoading={isLoading}
                error={error}
                emptyMessage="No notification templates"
                expandable
                expandedRowId={expandedRowId}
                onExpandedChange={id => setExpandedRowId(id)}
                getRowCanExpand={() => true}
                renderExpandedContent={row => (
                    <div className="space-y-2 p-4">
                        <h4 className="text-sm font-semibold">Rules</h4>
                        {row.rules.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No rules configured</p>
                        ) : (
                            <ul className="space-y-2">
                                {row.rules.map(rule => (
                                    <li key={rule.notificationRuleId} className="flex items-center justify-between rounded-md border p-2 text-sm">
                                        <span>
                                            {rule.active ? 'Active' : 'Inactive'} —{' '}
                                            {rule.notificationRuleTemplateTokens ? JSON.stringify(rule.notificationRuleTemplateTokens) : 'Default'}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-destructive"
                                            onClick={() => rule.notificationRuleId && onDeleteRule(rule.notificationRuleId).then(() => onRefetch())}
                                        >
                                            Delete
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            />
        </div>
    );
}
